import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Star } from 'lucide-react';
import styles from './PortfolioTestimonials.module.scss';

const VISIBLE = 2;
// Quotes longer than this get clamped with "Show more".
const LONG_QUOTE = 220;

const Stars = ({ rating }) => (
  <span
    className={styles.stars}
    role="img"
    aria-label={`${rating} out of 5 stars`}
  >
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={14}
        strokeWidth={2}
        color={n <= rating ? '#FFB400' : '#CBD3DC'}
        fill={n <= rating ? '#FFB400' : 'none'}
        aria-hidden="true"
      />
    ))}
  </span>
);

Stars.propTypes = {
  rating: PropTypes.number,
};

const TestimonialCard = ({ testimonial, className }) => {
  const [open, setOpen] = useState(false);
  const text = testimonial.text || '';
  const isLong = text.length > LONG_QUOTE;
  const showCompany =
    testimonial.company_name && testimonial.company_name !== testimonial.name;

  return (
    <article className={`${styles.card} ${className || ''}`}>
      <div className={styles.cardTop}>
        <Stars rating={Math.round(testimonial.rating || 0)} />
        <span className={styles.date}>
          {moment(testimonial.created_at).format('MMM YYYY')}
        </span>
      </div>
      <p className={`${styles.quote} ${isLong && !open ? styles.clamped : ''}`}>
        “{text}”
      </p>
      {isLong && (
        <button
          type="button"
          className={styles.link}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
      <div className={styles.author}>
        <span className={styles.authorName}>
          {testimonial.name}
          {showCompany && ` · ${testimonial.company_name}`}
        </span>
        {testimonial.project_name && (
          <span className={styles.project}>{testimonial.project_name}</span>
        )}
      </div>
    </article>
  );
};

TestimonialCard.propTypes = {
  testimonial: PropTypes.object.isRequired,
  className: PropTypes.string,
};

const PortfolioTestimonials = ({
  testimonials,
  total,
  user,
  isOwn,
  onOpen,
}) => {
  const count = Math.max(total || 0, testimonials.length);
  const pending = isOwn ? user.stats?.num_of_testimonials_pending || 0 : 0;

  if (!isOwn && count === 0) {
    return null;
  }

  if (isOwn && count === 0 && pending === 0) {
    return (
      <section id="portfolio-testimonials" className={styles.dashed}>
        <div className={styles.header}>
          <h2 className={`${styles.title} ${styles.mutedTitle}`}>
            Testimonials
          </h2>
          <span className={styles.hiddenBadge}>Hidden until added</span>
        </div>
        <p className={styles.placeholder}>
          Testimonials from past clients help you get hired.{' '}
          <button type="button" className={styles.link} onClick={onOpen}>
            Ask a past client for one
          </button>
        </p>
      </section>
    );
  }

  const avg = user.rating?.avg;
  const reviewCount = user.rating?.count || 0;

  return (
    <section
      id="portfolio-testimonials"
      className={styles.section}
      aria-labelledby="portfolio-testimonials-title"
    >
      <div className={styles.header}>
        <h2 id="portfolio-testimonials-title" className={styles.title}>
          Testimonials
          {isOwn ? (
            <span className={styles.count}>
              {' '}
              · {count} live{pending > 0 ? `, ${pending} pending` : ''}
            </span>
          ) : (
            avg > 0 &&
            reviewCount > 0 && (
              <span className={styles.count}>
                {' '}
                · {avg.toFixed(1)} from {reviewCount}{' '}
                {reviewCount === 1 ? 'review' : 'reviews'}
              </span>
            )
          )}
        </h2>
        {isOwn ? (
          <button type="button" className={styles.manage} onClick={onOpen}>
            Manage testimonials
          </button>
        ) : (
          count > VISIBLE && (
            <button type="button" className={styles.link} onClick={onOpen}>
              See all {count}
            </button>
          )
        )}
      </div>
      <div className={styles.grid}>
        {testimonials.slice(0, VISIBLE).map((testimonial, idx) => (
          <TestimonialCard
            key={testimonial.id}
            testimonial={testimonial}
            className={idx > 0 ? styles.hiddenOnPhone : ''}
          />
        ))}
        {pending > 0 && (
          <button type="button" className={styles.pendingTile} onClick={onOpen}>
            <span className={styles.pendingTitle}>
              {pending} {pending === 1 ? 'testimonial' : 'testimonials'} waiting
              for approval
            </span>
            <span className={styles.pendingText}>
              Approve them to show on your portfolio.
            </span>
          </button>
        )}
      </div>
    </section>
  );
};

PortfolioTestimonials.propTypes = {
  testimonials: PropTypes.array,
  total: PropTypes.number,
  user: PropTypes.object.isRequired,
  isOwn: PropTypes.bool,
  onOpen: PropTypes.func,
};

PortfolioTestimonials.defaultProps = {
  testimonials: [],
};

export default PortfolioTestimonials;
